// Módulo 1: nueva solicitud. Dos modos: pegar el mensaje tal como llegó (la IA
// o la heurística proponen; Mercadotecnia aprueba) o llenar el brief mínimo.
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import {
  Boton,
  Campo,
  Esqueleto,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Tabs,
  TabsContenido,
  TabsDisparador,
  TabsLista,
  Textarea,
  TituloPagina,
  Vacio,
} from "@iavmx/ui";
import { useAccion, useCatalogo, useConSesion, useEsMercadotecnia, type Estructurada } from "./consultas";
import { BRIEF_VACIO, faltantesDe, FormularioBrief, type Brief } from "./FormularioBrief";
import { Migas, Nota } from "./componentes";
import { ETIQUETA_CAMPO } from "./util";

const CANALES = [
  ["whatsapp", "WhatsApp"],
  ["slack", "Slack"],
  ["correo", "Correo"],
  ["junta", "Junta"],
  ["verbal", "Verbal"],
] as const;

function briefDesde(p: Estructurada["propuesta"]): Brief {
  return {
    titulo: p.titulo,
    objetivo: p.objetivo,
    publico: p.publico,
    productos: p.productos,
    fechaPublicacion: p.fechaPublicacion,
    areaSolicitante: p.areaSolicitante,
    solicitanteId: null,
    entregables: p.entregables,
    presupuesto: p.presupuesto,
    prioridad: p.prioridad,
    materialesDelSolicitante: p.materialesDelSolicitante,
  };
}

export function NuevaSolicitud() {
  const conSesion = useConSesion();
  const esMkt = useEsMercadotecnia();
  const cat = useCatalogo();
  const navegar = useNavigate();
  const [modo, setModo] = React.useState<"pegar" | "formulario">("pegar");
  const [texto, setTexto] = React.useState("");
  const [canal, setCanal] = React.useState<(typeof CANALES)[number][0]>("whatsapp");
  const [propuesta, setPropuesta] = React.useState<Estructurada | null>(null);
  const [brief, setBrief] = React.useState<Brief>(BRIEF_VACIO);
  const panelPropuesta = React.useRef<HTMLElement>(null);

  const estructurar = useAccion<{ texto: string }, Estructurada>({
    fn: (api, v) => api.api.solicitudes.estructurar.$post({ json: { texto: v.texto, canalOrigen: canal } }),
    onExito: (r) => {
      setPropuesta(r);
      setBrief(briefDesde(r.propuesta));
      // En celular la propuesta queda abajo del mensaje: se lleva la vista hasta ella.
      if (window.matchMedia("(max-width: 1023px)").matches)
        requestAnimationFrame(() => panelPropuesta.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    },
  });
  const aprobar = useAccion<{ propuestaId: string; brief: Brief }, { id: string; estado: string; camposFaltantes: string[] }>({
    fn: (api, v) =>
      api.api.solicitudes.propuestas[":id"].aprobar.$post({
        param: { id: v.propuestaId },
        json: {
          propuesta: {
            ...v.brief,
            tipo: "campana",
            camposFaltantes: faltantesDe(v.brief),
            completa: faltantesDe(v.brief).length === 0,
            resumen: "",
            entregables: v.brief.entregables.map((e) => ({ ...e, tipo: e.tipo as never })),
          },
          solicitanteId: v.brief.solicitanteId,
          canalOrigen: canal,
        },
      }),
    invalidar: ["bandeja"],
    exito: (r) => (r.estado === "incompleta" ? "Guardada como Incompleta; se pidió lo que falta." : "Solicitud aprobada y lista para armar la campaña."),
    onExito: (r) => void navegar({ to: "/campanas/solicitudes/$id", params: { id: r.id } }),
  });
  const rechazar = useAccion<{ propuestaId: string }>({
    fn: (api, v) => api.api.solicitudes.propuestas[":id"].rechazar.$post({ param: { id: v.propuestaId }, json: {} }),
    exito: "Propuesta descartada.",
    onExito: () => {
      setPropuesta(null);
      setBrief(BRIEF_VACIO);
    },
  });
  const guardar = useAccion<Brief, { id: string; estado: string }>({
    fn: (api, b) =>
      api.api.solicitudes.$post({
        json: { ...b, canalOrigen: "formulario", entregables: b.entregables.map((e) => ({ ...e, tipo: e.tipo as never })) },
      }),
    invalidar: ["bandeja"],
    exito: (r) => (r.estado === "incompleta" ? "Guardada como Incompleta; falta parte del brief." : "Solicitud guardada."),
    onExito: (r) => void navegar({ to: "/campanas/solicitudes/$id", params: { id: r.id } }),
  });

  if (!conSesion) return <Vacio titulo="Entra para pedir una campaña" />;
  if (cat.isLoading) return <Esqueleto className="h-64" />;
  if (cat.error || !cat.data) return <Vacio titulo="No se pudo cargar el catálogo" texto={cat.error?.message} />;
  const faltan = faltantesDe(brief);

  return (
    <>
      <Migas partes={[{ etiqueta: "Solicitudes", to: "/campanas" }, { etiqueta: "Nueva solicitud" }]} />
      <TituloPagina descripcion="Captura el brief mínimo o pega el mensaje tal como llegó; la IA propone y tú apruebas.">Nueva solicitud</TituloPagina>

      <Tabs value={modo} onValueChange={(v) => setModo(v as typeof modo)}>
        <TabsLista className="h-auto md:h-10">
          <TabsDisparador value="pegar" className="min-h-11 md:min-h-0">Pegar mensaje o correo</TabsDisparador>
          <TabsDisparador value="formulario" className="min-h-11 md:min-h-0">Formulario</TabsDisparador>
        </TabsLista>

        <TabsContenido value="pegar">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <section className="grid content-start gap-4">
              <Campo etiqueta="Mensaje original" htmlFor="mensaje" ayuda="WhatsApp, Slack o correo. Se guarda junto a la solicitud como referencia.">
                <Textarea
                  id="mensaje"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Hola Nai, para el aniversario queremos…"
                  className="min-h-56 text-base"
                  disabled={Boolean(propuesta)}
                />
              </Campo>
              <div className="flex flex-wrap items-end gap-3">
                <Campo etiqueta="Llegó por" htmlFor="canal" className="w-40">
                  <Select value={canal} onValueChange={(v) => setCanal(v as typeof canal)}>
                    <SelectDisparador id="canal" className="text-base">
                      <SelectValor />
                    </SelectDisparador>
                    <SelectContenido>
                      {CANALES.map(([c, n]) => (
                        <SelectOpcion key={c} value={c}>
                          {n}
                        </SelectOpcion>
                      ))}
                    </SelectContenido>
                  </Select>
                </Campo>
                {propuesta ? (
                  <Boton variante="contorno" onClick={() => setPropuesta(null)}>
                    Volver a proponer
                  </Boton>
                ) : (
                  <Boton variante="acento" disabled={texto.trim().length < 10 || estructurar.isPending} onClick={() => estructurar.mutate({ texto })}>
                    <Sparkles /> {estructurar.isPending ? "Leyendo el mensaje…" : "Proponer solicitud"}
                  </Boton>
                )}
              </div>
              <Nota>
                <p className="font-semibold text-tinta">Cómo funciona</p>
                <p className="mt-1">La IA clasifica, detecta lo que falta y propone entregables, áreas y fechas. No asigna, no aprueba y no publica: eso lo hace una persona.</p>
                <p className="mt-1">Una solicitud incompleta queda como Incompleta y no pasa a producción hasta que el solicitante complete los campos marcados.</p>
              </Nota>
            </section>

            <section aria-live="polite" ref={panelPropuesta} className="scroll-mt-4">
              {estructurar.isPending ? (
                <div className="grid gap-3" aria-busy="true">
                  <Esqueleto className="h-6 w-1/2" />
                  <Esqueleto className="h-10" />
                  <Esqueleto className="h-10" />
                  <Esqueleto className="h-24" />
                </div>
              ) : propuesta ? (
                <div className="grid gap-4 border border-linea p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="font-titulo text-xl font-bold">Solicitud propuesta</h2>
                    <span className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">
                      {propuesta.sinIa ? "Propuesta sin IA" : "Propuesta de la IA"}
                      {faltan.length > 0 ? ` · faltan ${faltan.length} ${faltan.length === 1 ? "campo" : "campos"} del brief mínimo` : " · brief completo"}
                    </span>
                  </div>
                  {propuesta.sinIa && (
                    <Nota tono="ambar">
                      Esta propuesta salió de reglas de texto, sin modelo de IA ({propuesta.motivoSinIa ?? "sin llave de OpenAI"}). Revísala con más cuidado antes de aprobar.
                    </Nota>
                  )}
                  <FormularioBrief valor={brief} onChange={setBrief} catalogo={cat.data} idBase="propuesta" verPresupuesto={esMkt} />
                  <Nota tono={faltan.length > 0 ? "ambar" : "azul"}>
                    {faltan.length > 0
                      ? `Al aprobar, la solicitud entra a la bandeja como Incompleta y se pide por correo ${faltan.map((c) => ETIQUETA_CAMPO[c]?.toLowerCase()).join(", ")}. Se asigna cuando esté completa.`
                      : "Al aprobar, la solicitud entra a la bandeja lista para armar la campaña."}
                  </Nota>
                  {!esMkt && <Nota>Solo Mercadotecnia aprueba propuestas. Si no eres de Mercadotecnia, usa el formulario.</Nota>}
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Boton variante="contorno" disabled={rechazar.isPending} onClick={() => rechazar.mutate({ propuestaId: propuesta.propuestaId })}>
                      Descartar
                    </Boton>
                    <Boton variante="acento" disabled={!esMkt || !brief.titulo.trim() || aprobar.isPending} onClick={() => aprobar.mutate({ propuestaId: propuesta.propuestaId, brief })}>
                      {aprobar.isPending ? "Guardando…" : faltan.length > 0 ? "Aprobar y pedir lo que falta" : "Aprobar solicitud"}
                    </Boton>
                  </div>
                </div>
              ) : (
                <Vacio titulo="Aquí aparece la propuesta" texto="Pega el mensaje y pide la propuesta. Vas a poder corregir cada campo antes de aprobar." />
              )}
            </section>
          </div>
        </TabsContenido>

        <TabsContenido value="formulario">
          <form
            className="grid max-w-3xl gap-6"
            onSubmit={(e) => {
              e.preventDefault();
              if (!brief.titulo.trim()) return;
              guardar.mutate(brief);
            }}
          >
            <FormularioBrief valor={brief} onChange={setBrief} catalogo={cat.data} idBase="form" verPresupuesto={esMkt} />
            <Nota tono={faltan.length > 0 ? "ambar" : "azul"}>
              {faltan.length > 0
                ? `Faltan ${faltan.map((c) => ETIQUETA_CAMPO[c]?.toLowerCase()).join(", ")}. Puedes guardar igual: quedará como Incompleta y no se asigna hasta completarla.`
                : "Brief completo. Al guardar queda lista para que Mercadotecnia arme la campaña."}
            </Nota>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Boton type="button" variante="contorno" onClick={() => void navegar({ to: "/campanas" })}>
                Cancelar
              </Boton>
              <Boton type="submit" variante="acento" disabled={!brief.titulo.trim() || guardar.isPending}>
                {guardar.isPending ? "Guardando…" : "Guardar solicitud"}
              </Boton>
            </div>
          </form>
        </TabsContenido>
      </Tabs>
    </>
  );
}
